import React from "react";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveFilterResources,
} from "@planzone/view-models";
import { resourcesListingFilter } from "@planzone/dto";

import { Flag, InfiniteScroll } from "@common/infiniteScroll";
import { Loader } from "@common/loader";
import { Props } from "./types";
import styles from "./styles.scss";

const SEARCH_DELAY = 300;

// Lists the resources searched and paginated by the back, loading the next page on scroll, for the menus
// that pick resources without the whole list. They share the filters state: only one is opened at a time.
function PagedResources({
  flagName,
  search = "",
  project,
  className,
  isScrolledByParent = false,
  loader,
  children,
}: Props) {
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    resources,
    isRequestPending,
    filter,
    totalPages,
    projectUuid,
    isDirty,
    isRequestFailure,
    retrieveFilterResources,
  } = useRetrieveFilterResources();
  const isSameList =
    projectUuid === (project?.uuid ?? null) && (filter.name ?? "") === search;

  // The list kept since the last opening is shown again, unless it was never loaded or another one is searched.
  React.useEffect(() => {
    if (isSameList && filter.page > 0 && !isRequestFailure) return;

    const timeout = window.setTimeout(
      () =>
        retrieveFilterResources({
          planzone,
          project,
          filter: { ...resourcesListingFilter, name: search },
        }),
      SEARCH_DELAY,
    );

    return () => window.clearTimeout(timeout);
  }, [search, project?.uuid]);

  // A dirty list is reloaded as far as it was scrolled, as the time sheets do, also when it is searched again.
  React.useEffect(() => {
    isDirty &&
      isSameList &&
      !isRequestPending &&
      retrieveFilterResources({
        planzone,
        project,
        filter: {
          ...filter,
          page: 0,
          size: Math.max(resources.length, resourcesListingFilter.size),
        },
      });
  }, [isDirty, isRequestPending, search, project?.uuid]);

  return (
    <div className={className}>
      <InfiniteScroll
        canFetchOnIdle={!isRequestPending}
        className={isScrolledByParent ? styles.grownContent : undefined}
      >
        {() => (
          <div className={styles.pagedResources}>
            {children(resources)}
            {isRequestPending &&
              (loader ?? (
                <div className={styles.loader}>
                  <Loader />
                </div>
              ))}
            <Flag
              name={flagName}
              retrieveItems={() =>
                !isRequestPending &&
                retrieveFilterResources({ planzone, project, filter })
              }
              isDisabled={
                isRequestPending ||
                filter.page === 0 ||
                filter.page >= totalPages
              }
            />
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
}

export { PagedResources };
